import { useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";
import { MessageCircle, Loader2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";

// Required, one-time step after subscribing: WhatsApp number with country code.
export default function WhatsAppStep() {
  const { refresh } = useAuth();
  const { lang } = useI18n();
  const en = lang === "en";
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone || !isValidPhoneNumber(phone)) {
      setErr(en ? "Enter a valid number for the selected country" : "أدخل رقمًا صحيحًا حسب الدولة المختارة");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/me/whatsapp", { phone });
      await refresh();
    } catch (ex) {
      setErr(ex.response?.data?.detail || (en ? "Couldn't save, try again" : "تعذّر الحفظ، حاول مرة ثانية"));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F4EC] flex items-center justify-center p-5" dir={en ? "ltr" : "rtl"} data-testid="whatsapp-step">
      <form onSubmit={submit} className="w-full max-w-md space-y-6">
        <div className="w-16 h-16 rounded-3xl bg-emerald-500 text-white grid place-items-center shadow-lg mx-auto">
          <MessageCircle className="w-8 h-8" />
        </div>
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-black text-neutral-900">{en ? "Your WhatsApp number" : "رقم الواتساب"}</h1>
          <p className="text-sm text-neutral-600 leading-relaxed">
            {en ? "So if you ever need support, we'll know you're a subscriber." : "اذا احتجت دعم نعرف انك مشترك في التطبيق"}
          </p>
        </div>
        <div className={`rounded-2xl border bg-white transition-colors ${err ? "border-red-300" : "border-neutral-200 focus-within:border-brand-500"}`} dir="ltr">
          <PhoneInput
            international
            defaultCountry="SA"
            flags={flags}
            countryCallingCodeEditable={false}
            value={phone}
            onChange={(v) => { setPhone(v || ""); setErr(""); }}
            placeholder="5X XXX XXXX"
            className="calofay-phone px-4 py-3.5 text-lg"
            data-testid="whatsapp-input"
          />
        </div>
        {err && (
          <div className="text-xs text-red-600 flex items-center gap-1" data-testid="whatsapp-error">
            <AlertCircle className="w-3.5 h-3.5" /> {err}
          </div>
        )}
        <button type="submit" disabled={saving}
          className="w-full orange-gradient text-white font-bold text-lg py-4 rounded-full shadow-brand flex items-center justify-center gap-2 disabled:opacity-70 active:scale-[0.98] transition-transform"
          data-testid="whatsapp-submit">
          {saving && <Loader2 className="w-5 h-5 animate-spin" />}
          {en ? "Continue" : "متابعة"}
        </button>
      </form>
    </div>
  );
}
